import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-hot-toast";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Copy,
  Eye,
  KeyRound,
  LayoutTemplate,
  Plus,
  Radio,
  RefreshCw,
  Save,
  Settings2,
  Trash2,
  Building2,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import {
  TenantUpdate,
  TrackedAccount,
  TrackedAccountCreateRequest,
  TrackedAccountListResponse,
  TrackedAccountUpdateRequest,
  TimelineDeleteResponse,
  TimelineResponse,
  TimelineTweet,
  TweetMedia,
  WidgetConfig,
  WidgetRenderMode,
  XApiCredential,
  XApiCredentialCreateRequest,
  XApiCredentialListResponse,
} from "@/types";

const tenantUpdateSchema = z.object({
  name: z.string().min(2, "テナント名は2文字以上で入力してください"),
  email: z.string().email("有効なメールアドレスを入力してください"),
  subdomain: z
    .string()
    .optional()
    .refine((value) => !value || /^[a-z0-9-]+$/.test(value), {
      message: "サブドメインは英小文字、数字、ハイフンのみ使用できます",
    }),
});

type TenantUpdateFormData = z.infer<typeof tenantUpdateSchema>;

const initialTrackedAccount: TrackedAccountCreateRequest = {
  screen_name: "",
  display_name: "",
  is_active: true,
  widget_enabled: true,
  sync_interval_minutes: 30,
  manual_cooldown_minutes: 15,
  max_posts_per_sync: 10,
  daily_post_cap: 100,
  monthly_post_cap: 3000,
};

const settingsTabs = [
  {
    id: "general",
    label: "基本情報",
    description: "tenant 名・メール・サブドメイン",
    icon: Building2,
  },
  {
    id: "x-api",
    label: "X API",
    description: "Bearer Token の登録と切替",
    icon: Settings2,
  },
  {
    id: "accounts",
    label: "Tracked Accounts",
    description: "取得対象とコスト上限の管理",
    icon: Radio,
  },
  {
    id: "widget",
    label: "Widget",
    description: "表示設定と埋め込みコード",
    icon: LayoutTemplate,
  },
] as const;

type SettingsTabId = (typeof settingsTabs)[number]["id"];

export const SettingsPage = () => {
  const accountTweetsPageSize = 10;
  const queryClient = useQueryClient();
  const { tenant, fetchProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState<string | null>(null);
  const [credentialForm, setCredentialForm] = useState<XApiCredentialCreateRequest>({
    name: "",
    bearer_token: "",
  });
  const [trackedAccountForm, setTrackedAccountForm] =
    useState<TrackedAccountCreateRequest>(initialTrackedAccount);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [accountTweetsPageById, setAccountTweetsPageById] = useState<Record<string, number>>({});
  const [selectedTweetIdsByAccount, setSelectedTweetIdsByAccount] = useState<Record<string, string[]>>({});
  const [widgetForm, setWidgetForm] = useState<WidgetConfig | null>(null);
  const [syncingAccountId, setSyncingAccountId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<TenantUpdateFormData>({
    resolver: zodResolver(tenantUpdateSchema),
    defaultValues: {
      name: tenant?.name || "",
      email: tenant?.email || "",
      subdomain: tenant?.subdomain || "",
    },
  });

  const requestedTab = searchParams.get("tab") as SettingsTabId | null;
  const activeTab: SettingsTabId = settingsTabs.some((tab) => tab.id === requestedTab)
    ? (requestedTab as SettingsTabId)
    : "general";
  const shouldLiveRefreshAccounts = activeTab === "accounts" || activeTab === "widget";

  const {
    data: credentialsData,
    isLoading: credentialsLoading,
  } = useQuery({
    queryKey: ["x-api-credentials"],
    queryFn: () => api.get<XApiCredentialListResponse>("/x-api/credentials"),
    refetchOnWindowFocus: true,
  });

  const {
    data: trackedAccountsData,
    isLoading: accountsLoading,
  } = useQuery({
    queryKey: ["tracked-accounts"],
    queryFn: () => api.get<TrackedAccountListResponse>("/tracked-accounts"),
    refetchOnWindowFocus: true,
    refetchInterval: shouldLiveRefreshAccounts ? 5000 : false,
  });

  const {
    data: widgetConfig,
    isLoading: widgetConfigLoading,
  } = useQuery({
    queryKey: ["widget-config"],
    queryFn: () => api.get<WidgetConfig>("/widget/config"),
    refetchOnWindowFocus: true,
  });

  const expandedAccountPage = expandedAccountId ? (accountTweetsPageById[expandedAccountId] || 1) : 1;
  const {
    data: expandedAccountTweets,
    isLoading: accountTweetsLoading,
  } = useQuery({
    queryKey: ["tracked-account-tweets", expandedAccountId, expandedAccountPage],
    queryFn: () =>
      api.get<TimelineResponse>("/timeline/tweets", {
        tracked_account_id: expandedAccountId,
        page: expandedAccountPage,
        page_size: accountTweetsPageSize,
        exclude_retweets: false,
      }),
    enabled: activeTab === "accounts" && !!expandedAccountId,
    refetchOnWindowFocus: true,
  });

  const updateTenantMutation = useMutation({
    mutationFn: (payload: TenantUpdate) => api.put("/tenants/profile", payload),
    onSuccess: () => {
      toast.success("テナント情報を更新しました");
      fetchProfile();
      reset(undefined, { keepValues: true });
    },
    onError: (error) => {
      console.error("Update tenant error:", error);
      toast.error("テナント情報の更新に失敗しました");
    },
  });

  const createCredentialMutation = useMutation({
    mutationFn: (payload: XApiCredentialCreateRequest) =>
      api.post<XApiCredential>("/x-api/credentials", payload),
    onSuccess: () => {
      toast.success("X API credential を追加しました");
      setCredentialForm({ name: "", bearer_token: "" });
      queryClient.invalidateQueries({ queryKey: ["x-api-credentials"] });
    },
    onError: (error) => {
      console.error("Create credential error:", error);
      toast.error("credential の追加に失敗しました");
    },
  });

  const deleteCredentialMutation = useMutation({
    mutationFn: (credentialId: string) => api.delete(`/x-api/credentials/${credentialId}`),
    onSuccess: () => {
      toast.success("credential を削除しました");
      queryClient.invalidateQueries({ queryKey: ["x-api-credentials"] });
    },
    onError: (error) => {
      console.error("Delete credential error:", error);
      toast.error("credential の削除に失敗しました");
    },
  });

  const createTrackedAccountMutation = useMutation({
    mutationFn: (payload: TrackedAccountCreateRequest) =>
      api.post<TrackedAccount>("/tracked-accounts", payload),
    onSuccess: () => {
      toast.success("Tracked Account を追加しました");
      setTrackedAccountForm(initialTrackedAccount);
      queryClient.invalidateQueries({ queryKey: ["tracked-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
    },
    onError: (error) => {
      console.error("Create tracked account error:", error);
      toast.error("Tracked Account の追加に失敗しました");
    },
  });

  const updateTrackedAccountMutation = useMutation({
    mutationFn: ({
      accountId,
      payload,
    }: {
      accountId: string;
      payload: TrackedAccountUpdateRequest;
    }) => api.put<TrackedAccount>(`/tracked-accounts/${accountId}`, payload),
    onSuccess: () => {
      toast.success("Tracked Account を更新しました");
      setEditingAccountId(null);
      queryClient.invalidateQueries({ queryKey: ["tracked-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
    },
    onError: (error) => {
      console.error("Update tracked account error:", error);
      toast.error("Tracked Account の更新に失敗しました");
    },
  });

  const deleteTrackedAccountMutation = useMutation({
    mutationFn: (accountId: string) => api.delete(`/tracked-accounts/${accountId}`),
    onSuccess: () => {
      toast.success("Tracked Account を削除しました");
      setExpandedAccountId(null);
      queryClient.invalidateQueries({ queryKey: ["tracked-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
    },
    onError: (error) => {
      console.error("Delete tracked account error:", error);
      toast.error("Tracked Account の削除に失敗しました");
    },
  });

  const deleteAccountTweetsMutation = useMutation({
    mutationFn: ({
      accountId,
      tweetIds,
    }: {
      accountId: string;
      tweetIds: string[];
    }) =>
      api.delete<TimelineDeleteResponse>("/timeline/tweets", {
        tracked_account_id: accountId,
        tweet_ids: tweetIds,
      }),
    onSuccess: async (response, variables) => {
      toast.success(`${response.deleted_count} 件の投稿を削除しました`);
      setSelectedTweetIdsByAccount((current) => ({
        ...current,
        [variables.accountId]: [],
      }));
      queryClient.invalidateQueries({ queryKey: ["tracked-account-tweets", variables.accountId] });
      queryClient.invalidateQueries({ queryKey: ["tracked-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
    },
    onError: (error) => {
      console.error("Delete tracked account tweets error:", error);
      toast.error("投稿の削除に失敗しました");
    },
  });

  const syncTrackedAccountMutation = useMutation({
    mutationFn: (account: TrackedAccount) =>
      api.post(`/tracked-accounts/${account.id}/sync`),
    onMutate: (account) => {
      setSyncingAccountId(account.id);
    },
    onSuccess: async (_response, account) => {
      toast.success(`@${account.screen_name} を手動更新しました`);
      queryClient.invalidateQueries({ queryKey: ["tracked-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-data"] });
      queryClient.invalidateQueries({ queryKey: ["tracked-account-tweets", account.id] });
    },
    onError: (error, account) => {
      console.error("Sync tracked account error:", error);
      toast.error(`@${account.screen_name} の手動更新に失敗しました`);
    },
    onSettled: () => {
      setSyncingAccountId(null);
    },
  });

  const updateWidgetConfigMutation = useMutation({
    mutationFn: (payload: WidgetConfig) => api.put<WidgetConfig>("/widget/config", payload),
    onSuccess: () => {
      toast.success("Widget 設定を更新しました");
      queryClient.invalidateQueries({ queryKey: ["widget-config"] });
    },
    onError: (error) => {
      console.error("Update widget config error:", error);
      toast.error("Widget 設定の更新に失敗しました");
    },
  });

  const credentials = credentialsData?.credentials || [];
  const trackedAccounts = trackedAccountsData?.accounts || [];
  const activeTabMeta =
    settingsTabs.find((tab) => tab.id === activeTab) || settingsTabs[0];

  const widgetBaseUrl = useMemo(() => {
    return widgetForm?.script_base_url || widgetConfig?.script_base_url || `${window.location.origin}/widget.js`;
  }, [widgetConfig?.script_base_url, widgetForm?.script_base_url]);

  useEffect(() => {
    if (widgetConfig) {
      setWidgetForm({
        ...widgetConfig,
        render_mode: widgetConfig.render_mode || "custom",
      });
    }
  }, [widgetConfig]);

  useEffect(() => {
    if (!expandedAccountId || !expandedAccountTweets) {
      return;
    }

    const totalPages = Math.max(
      1,
      Math.ceil(expandedAccountTweets.total_count / expandedAccountTweets.page_size)
    );
    if (expandedAccountPage > totalPages) {
      setExpandedAccountPageNumber(expandedAccountId, totalPages);
    }
  }, [expandedAccountId, expandedAccountPage, expandedAccountTweets]);

  const onTenantSubmit = (data: TenantUpdateFormData) => {
    updateTenantMutation.mutate(data);
  };

  const copyText = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
      toast.success("クリップボードにコピーしました");
    } catch (error) {
      console.error("Copy error:", error);
      toast.error("コピーに失敗しました");
    }
  };

  const startEditAccount = (account: TrackedAccount) => {
    setEditingAccountId(account.id);
    setTrackedAccountForm({
      screen_name: account.screen_name,
      display_name: account.display_name,
      x_user_id: account.x_user_id,
      is_active: account.is_active,
      widget_enabled: account.widget_enabled,
      sync_interval_minutes: account.sync_interval_minutes,
      manual_cooldown_minutes: account.manual_cooldown_minutes,
      max_posts_per_sync: account.max_posts_per_sync,
      daily_post_cap: account.daily_post_cap,
      monthly_post_cap: account.monthly_post_cap,
    });
  };

  const resetTrackedAccountForm = () => {
    setEditingAccountId(null);
    setTrackedAccountForm(initialTrackedAccount);
  };

  const submitTrackedAccount = () => {
    if (!trackedAccountForm.screen_name?.trim()) {
      toast.error("screen name を入力してください");
      return;
    }

    if (editingAccountId) {
      updateTrackedAccountMutation.mutate({
        accountId: editingAccountId,
        payload: trackedAccountForm,
      });
      return;
    }

    createTrackedAccountMutation.mutate(trackedAccountForm);
  };

  const widgetSnippet = (screenName: string) =>
    (() => {
      const params = new URLSearchParams({
        user: screenName,
        api_key: tenant?.api_key || "YOUR_API_KEY",
        count: String(widgetForm?.max_tweets || 5),
        mode: widgetForm?.render_mode || "custom",
        theme: widgetForm?.theme || "default",
        width: String(widgetForm?.width || 550),
      });
      if (widgetForm?.show_retweets) {
        params.set("include_retweets", "true");
      }
      if (widgetForm?.height) {
        params.set("height", String(widgetForm.height));
      }
      return `<script>(function(){var s=document.createElement('script');s.src='${widgetBaseUrl}?${params.toString()}&_cb=' + Date.now();s.async=true;document.currentScript.parentNode.insertBefore(s, document.currentScript);})();</script>`;
    })();

  const expandedTweets = expandedAccountTweets?.tweets || [];
  const expandedTweetIds = expandedAccountId ? (selectedTweetIdsByAccount[expandedAccountId] || []) : [];
  const expandedTweetIdSet = new Set(expandedTweetIds);
  const expandedTotalPages = expandedAccountTweets
    ? Math.max(1, Math.ceil(expandedAccountTweets.total_count / expandedAccountTweets.page_size))
    : 1;
  const areAllExpandedTweetsSelected =
    expandedTweets.length > 0 && expandedTweets.every((tweet) => expandedTweetIdSet.has(tweet.id));

  const formatTweetDate = (value?: string) => {
    if (!value) return "不明";
    try {
      return new Date(value).toLocaleString("ja-JP");
    } catch {
      return value;
    }
  };

  const renderTweetMedia = (media: TweetMedia[] = [], fallbackLink?: string, fallbackAlt?: string) => {
    if (!media.length) {
      return null;
    }

    const gridClass = media.length === 1 ? "grid-cols-1" : "grid-cols-2";

    return (
      <div className={`mt-3 grid gap-2 ${gridClass}`}>
        {media.map((item, index) => {
          if (item.type === "video" && item.url) {
            return (
              <div
                key={`${item.url}-${index}`}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950"
              >
                <video
                  className="h-full max-h-96 w-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  poster={item.poster_url || undefined}
                >
                  <source src={item.url} />
                </video>
              </div>
            );
          }

          const imageUrl = item.url || item.poster_url;
          if (!imageUrl) {
            return null;
          }

          return (
            <a
              key={`${imageUrl}-${index}`}
              href={fallbackLink}
              target="_blank"
              rel="noreferrer"
              className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100"
            >
              <img
                src={imageUrl}
                alt={item.alt_text || fallbackAlt || "tweet media"}
                className="h-full max-h-96 w-full object-cover"
                loading="lazy"
              />
            </a>
          );
        })}
      </div>
    );
  };

  const toggleAccountTimeline = (accountId: string) => {
    setExpandedAccountId((current) => {
      if (current === accountId) {
        return null;
      }
      return accountId;
    });
    setAccountTweetsPageById((current) => ({
      ...current,
      [accountId]: current[accountId] || 1,
    }));
  };

  const setExpandedAccountPageNumber = (accountId: string, page: number) => {
    setAccountTweetsPageById((current) => ({
      ...current,
      [accountId]: Math.max(1, page),
    }));
  };

  const toggleTweetSelection = (accountId: string, tweetId: string) => {
    setSelectedTweetIdsByAccount((current) => {
      const selectedIds = current[accountId] || [];
      const nextIds = selectedIds.includes(tweetId)
        ? selectedIds.filter((id) => id !== tweetId)
        : [...selectedIds, tweetId];
      return {
        ...current,
        [accountId]: nextIds,
      };
    });
  };

  const toggleExpandedPageSelection = () => {
    if (!expandedAccountId) {
      return;
    }
    setSelectedTweetIdsByAccount((current) => {
      const selectedIds = current[expandedAccountId] || [];
      const selectedSet = new Set(selectedIds);
      const nextIds = areAllExpandedTweetsSelected
        ? selectedIds.filter((id) => !expandedTweets.some((tweet) => tweet.id === id))
        : [
            ...selectedIds,
            ...expandedTweets
              .map((tweet) => tweet.id)
              .filter((tweetId) => !selectedSet.has(tweetId)),
          ];
      return {
        ...current,
        [expandedAccountId]: nextIds,
      };
    });
  };

  const deleteSelectedExpandedTweets = () => {
    if (!expandedAccountId || expandedTweetIds.length === 0) {
      toast.error("削除する投稿を選択してください");
      return;
    }

    deleteAccountTweetsMutation.mutate({
      accountId: expandedAccountId,
      tweetIds: expandedTweetIds,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{activeTabMeta.label}</h1>
          <p className="text-gray-600">
            {activeTabMeta.description}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">登録 credential</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{credentials.length}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">Tracked Accounts</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{trackedAccounts.length}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">公開APIキー</p>
            <p className="mt-2 text-sm font-medium text-gray-900 break-all">
              {tenant?.api_key || "未発行"}
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">Widget Script</p>
            <p className="mt-2 text-sm font-medium text-gray-900 break-all">{widgetBaseUrl}</p>
          </div>
        </div>

        {activeTab === "general" ? (
          <Card>
            <CardHeader>
              <h3 className="text-lg font-medium text-gray-900">tenant 基本情報</h3>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleSubmit(onTenantSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <Input
                    {...register("name")}
                    label="tenant 名"
                    error={errors.name?.message}
                  />
                  <Input
                    {...register("email")}
                    type="email"
                    label="メールアドレス"
                    error={errors.email?.message}
                  />
                  <Input
                    {...register("subdomain")}
                    label="サブドメイン"
                    helperText="widget や公開URLに利用されます"
                    error={errors.subdomain?.message}
                  />
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-sm text-gray-500">プラン</p>
                    <p className="text-base font-medium text-gray-900">
                      {tenant?.plan_id || "basic"}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    loading={updateTenantMutation.isPending}
                    disabled={!isDirty || updateTenantMutation.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    保存
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        ) : null}

        {activeTab === "x-api" ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">X API credentials</h3>
                <span className="text-sm text-gray-500">{credentials.length} 件</span>
              </div>
            </CardHeader>
            <CardBody className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr,2fr,auto]">
                <Input
                  label="表示名"
                  value={credentialForm.name}
                  onChange={(event) =>
                    setCredentialForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Shared App / Tenant Token"
                />
                <Input
                  label="Bearer Token"
                  value={credentialForm.bearer_token}
                  onChange={(event) =>
                    setCredentialForm((current) => ({
                      ...current,
                      bearer_token: event.target.value,
                    }))
                  }
                  placeholder="X API Bearer Token"
                />
                <div className="flex items-end">
                  <Button
                    fullWidth
                    onClick={() => createCredentialMutation.mutate(credentialForm)}
                    loading={createCredentialMutation.isPending}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    追加
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                tenant 固有 token を登録すると shared credential より優先されます。まずは 1 つだけ登録して疎通確認する運用を推奨します。
              </div>

              <div className="space-y-3">
                {credentialsLoading ? (
                  <p className="text-sm text-gray-500">読み込み中...</p>
                ) : credentials.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    まだ credential がありません。Tenant ごとの token または shared token を登録してください。
                  </p>
                ) : (
                  credentials.map((credential) => (
                    <div
                      key={credential.id}
                      className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{credential.name}</p>
                        <p className="text-sm text-gray-500">
                          {credential.owner_type === "platform" ? "Platform Shared" : "Tenant Owned"}
                          {" • "}
                          {credential.token_preview}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            credential.is_active
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {credential.is_active ? "有効" : "無効"}
                        </span>
                        <Button
                          variant="ghost"
                          onClick={() => deleteCredentialMutation.mutate(credential.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        ) : null}

        {activeTab === "accounts" ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">Tracked Accounts</h3>
                <span className="text-sm text-gray-500">{trackedAccounts.length} 件</span>
              </div>
            </CardHeader>
            <CardBody className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Input
                  label="X User ID（@xxxx）"
                  value={trackedAccountForm.screen_name || ""}
                  onChange={(event) =>
                    setTrackedAccountForm((current) => ({
                      ...current,
                      screen_name: event.target.value.replace(/^@/, ""),
                    }))
                  }
                  helperText="必須です。@ は付けても付けなくても登録できます"
                  placeholder="@openai"
                />
                <Input
                  label="表示名（管理用・任意）"
                  value={trackedAccountForm.display_name || ""}
                  onChange={(event) =>
                    setTrackedAccountForm((current) => ({
                      ...current,
                      display_name: event.target.value,
                    }))
                  }
                  helperText="管理画面で見分けるためのラベルです。未入力時は X の表示名を使います"
                  placeholder="OpenAI Japan"
                />
                {editingAccountId ? (
                  <Input
                    label="X 内部ユーザーID（管理用・自動取得）"
                    value={trackedAccountForm.x_user_id || ""}
                    helperText="登録時に自動取得される内部 ID です。通常は編集不要です"
                    readOnly
                    disabled
                  />
                ) : null}
                <Input
                  type="number"
                  label="同期間隔(分)"
                  value={trackedAccountForm.sync_interval_minutes || 30}
                  onChange={(event) =>
                    setTrackedAccountForm((current) => ({
                      ...current,
                      sync_interval_minutes: Number(event.target.value || 30),
                    }))
                  }
                />
                <Input
                  type="number"
                  label="手動クールダウン(分)"
                  value={trackedAccountForm.manual_cooldown_minutes || 15}
                  onChange={(event) =>
                    setTrackedAccountForm((current) => ({
                      ...current,
                      manual_cooldown_minutes: Number(event.target.value || 15),
                    }))
                  }
                />
                <Input
                  type="number"
                  label="1回取得上限"
                  value={trackedAccountForm.max_posts_per_sync || 10}
                  onChange={(event) =>
                    setTrackedAccountForm((current) => ({
                      ...current,
                      max_posts_per_sync: Number(event.target.value || 10),
                    }))
                  }
                />
                <Input
                  type="number"
                  label="日次上限"
                  value={trackedAccountForm.daily_post_cap || 100}
                  onChange={(event) =>
                    setTrackedAccountForm((current) => ({
                      ...current,
                      daily_post_cap: Number(event.target.value || 100),
                    }))
                  }
                />
                <Input
                  type="number"
                  label="月次上限"
                  value={trackedAccountForm.monthly_post_cap || 3000}
                  onChange={(event) =>
                    setTrackedAccountForm((current) => ({
                      ...current,
                      monthly_post_cap: Number(event.target.value || 3000),
                    }))
                  }
                />
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={trackedAccountForm.is_active ?? true}
                    onChange={(event) =>
                      setTrackedAccountForm((current) => ({
                        ...current,
                        is_active: event.target.checked,
                      }))
                    }
                  />
                  同期を有効化
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={trackedAccountForm.widget_enabled ?? true}
                    onChange={(event) =>
                      setTrackedAccountForm((current) => ({
                        ...current,
                        widget_enabled: event.target.checked,
                      }))
                    }
                  />
                  Widget で公開
                </label>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={submitTrackedAccount}
                  loading={
                    createTrackedAccountMutation.isPending ||
                    updateTrackedAccountMutation.isPending
                  }
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {editingAccountId ? "更新" : "追加"}
                </Button>
                {editingAccountId ? (
                  <Button variant="ghost" onClick={resetTrackedAccountForm}>
                    編集をキャンセル
                  </Button>
                ) : null}
              </div>

              <div className="space-y-3">
                {accountsLoading ? (
                  <p className="text-sm text-gray-500">読み込み中...</p>
                ) : trackedAccounts.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Tracked Account はまだありません。上のフォームから追加してください。
                  </p>
                ) : (
                  trackedAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="rounded-lg border border-gray-200 p-4"
                    >
                      {(() => {
                        const isExpanded = expandedAccountId === account.id;
                        const accountSelectedTweetIds = selectedTweetIdsByAccount[account.id] || [];

                        return (
                          <>
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <p className="font-medium text-gray-900">@{account.screen_name}</p>
                          <p className="text-sm text-gray-500">
                            {account.display_name || "表示名未設定"} • 保存投稿 {account.posts_stored} 件
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                            {account.sync_interval_minutes}分間隔
                          </span>
                          {account.widget_enabled ? (
                            <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-700">
                              Widget
                            </span>
                          ) : null}
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                            最終更新 {formatTweetDate(account.last_synced_at)}
                          </span>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => syncTrackedAccountMutation.mutate(account)}
                            loading={syncingAccountId === account.id}
                            disabled={syncingAccountId !== null && syncingAccountId !== account.id}
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            手動更新
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => toggleAccountTimeline(account.id)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            {isExpanded ? "投稿一覧を閉じる" : "投稿一覧を見る"}
                          </Button>
                          <Button variant="ghost" onClick={() => startEditAccount(account)}>
                            編集
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => deleteTrackedAccountMutation.mutate(account.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {account.last_sync_error ? (
                        <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                          {account.last_sync_error}
                        </p>
                      ) : null}
                      {isExpanded ? (
                        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                @{account.screen_name} の取得済み投稿
                              </p>
                              <p className="text-xs text-slate-500">
                                {expandedAccountTweets?.total_count || 0} 件中 {expandedTweets.length} 件を表示
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                  type="checkbox"
                                  checked={areAllExpandedTweetsSelected}
                                  onChange={toggleExpandedPageSelection}
                                  disabled={expandedTweets.length === 0}
                                />
                                このページを全選択
                              </label>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={deleteSelectedExpandedTweets}
                                disabled={
                                  accountSelectedTweetIds.length === 0 ||
                                  deleteAccountTweetsMutation.isPending
                                }
                                loading={deleteAccountTweetsMutation.isPending}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                選択削除
                              </Button>
                            </div>
                          </div>

                          <div className="mt-4 space-y-3">
                            {accountTweetsLoading ? (
                              <p className="text-sm text-slate-500">投稿を読み込み中...</p>
                            ) : expandedTweets.length === 0 ? (
                              <p className="text-sm text-slate-500">このアカウントの投稿はまだ保存されていません。</p>
                            ) : (
                              expandedTweets.map((tweet: TimelineTweet) => (
                                <div
                                  key={tweet.id}
                                  className="rounded-lg border border-slate-200 bg-white p-3"
                                >
                                  <div className="flex gap-3">
                                    <input
                                      type="checkbox"
                                      className="mt-1"
                                      checked={accountSelectedTweetIds.includes(tweet.id)}
                                      onChange={() => toggleTweetSelection(account.id, tweet.id)}
                                    />
                                    <div className="min-w-0 flex-1 space-y-2">
                                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                        <div className="text-xs text-slate-500">
                                          投稿日 {formatTweetDate(tweet.created_at)}
                                          {" • "}
                                          取得 {formatTweetDate(tweet.retrieved_at)}
                                        </div>
                                        <a
                                          href={tweet.permalink || `https://x.com/${tweet.user_screen_name}/status/${tweet.id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-xs font-medium text-primary-700 hover:text-primary-800"
                                        >
                                          Xで開く
                                        </a>
                                      </div>
                                      <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
                                        {tweet.text}
                                      </p>
                                      {renderTweetMedia(
                                        tweet.media,
                                        tweet.permalink || `https://x.com/${tweet.user_screen_name}/status/${tweet.id}`,
                                        tweet.text
                                      )}
                                      <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                        <span>返信 {tweet.reply_count}</span>
                                        <span>再投稿 {tweet.retweet_count}</span>
                                        <span>いいね {tweet.favorite_count}</span>
                                        <span>引用 {tweet.quote_count}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 md:flex-row md:items-center md:justify-between">
                            <p className="text-sm text-slate-500">
                              ページ {expandedAccountPage} / {expandedTotalPages}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setExpandedAccountPageNumber(account.id, expandedAccountPage - 1)
                                }
                                disabled={expandedAccountPage <= 1}
                              >
                                <ChevronLeft className="mr-1 h-4 w-4" />
                                前へ
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setExpandedAccountPageNumber(account.id, expandedAccountPage + 1)
                                }
                                disabled={expandedAccountPage >= expandedTotalPages}
                              >
                                次へ
                                <ChevronRight className="ml-1 h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : null}
                          </>
                        );
                      })()}
                    </div>
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        ) : null}

        {activeTab === "widget" ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-medium text-gray-900">Widget 設定</h3>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                  <KeyRound className="mr-1 inline h-3.5 w-3.5" />
                  API Key 連携
                </div>
              </div>
            </CardHeader>
            <CardBody className="space-y-5">
              {widgetConfigLoading || !widgetForm ? (
                <p className="text-sm text-gray-500">読み込み中...</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        テーマ
                      </label>
                      <select
                        value={widgetForm.theme}
                        onChange={(event) =>
                          setWidgetForm({
                            ...widgetForm,
                            theme: event.target.value as WidgetConfig["theme"],
                          })
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="default">default</option>
                        <option value="dark">dark</option>
                      </select>
                    </div>
                    <Input
                      type="number"
                      label="投稿件数"
                      value={widgetForm.max_tweets}
                      onChange={(event) =>
                        setWidgetForm({
                          ...widgetForm,
                          max_tweets: Number(event.target.value || 5),
                        })
                      }
                    />
                    <Input
                      type="number"
                      label="幅(px)"
                      value={widgetForm.width || 550}
                      onChange={(event) =>
                        setWidgetForm({
                          ...widgetForm,
                          width: Number(event.target.value || 550),
                        })
                      }
                    />
                    <Input
                      type="number"
                      label="高さ(px)"
                      value={widgetForm.height || 0}
                      onChange={(event) =>
                        setWidgetForm({
                          ...widgetForm,
                          height: Number(event.target.value || 0),
                        })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {([
                      {
                        value: "custom",
                        title: "自前描画モード",
                        description:
                          "保存済みポストを直接描画します。高速で、画像・動画もそのまま表示します。",
                      },
                      {
                        value: "embed",
                        title: "公式埋め込みモード",
                        description:
                          "X公式の埋め込みを使います。見た目は公式準拠ですが、外部 script に依存します。",
                      },
                    ] as {
                      value: WidgetRenderMode;
                      title: string;
                      description: string;
                    }[]).map((modeOption) => {
                      const isSelected = widgetForm.render_mode === modeOption.value;
                      return (
                        <button
                          key={modeOption.value}
                          type="button"
                          onClick={() =>
                            setWidgetForm({
                              ...widgetForm,
                              render_mode: modeOption.value,
                            })
                          }
                          className={`rounded-2xl border p-4 text-left transition ${
                            isSelected
                              ? "border-primary-500 bg-primary-50 shadow-sm"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-medium text-gray-900">{modeOption.title}</p>
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                isSelected
                                  ? "bg-primary-600 text-white"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {isSelected ? "選択中" : "未選択"}
                            </span>
                          </div>
                          <p className="mt-2 text-sm leading-6 text-gray-600">
                            {modeOption.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-6 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={widgetForm.show_retweets}
                        onChange={(event) =>
                          setWidgetForm({
                            ...widgetForm,
                            show_retweets: event.target.checked,
                          })
                        }
                      />
                      再投稿を含める
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={widgetForm.show_replies}
                        onChange={(event) =>
                          setWidgetForm({
                            ...widgetForm,
                            show_replies: event.target.checked,
                          })
                        }
                      />
                      返信を含める
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={widgetForm.auto_add_accounts}
                        onChange={(event) =>
                          setWidgetForm({
                            ...widgetForm,
                            auto_add_accounts: event.target.checked,
                          })
                        }
                      />
                      埋め込みアクセス時に未登録アカウントを自動追加
                    </label>
                  </div>

                  <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                    この設定を有効にすると、`widget.js` への初回アクセス時に未登録の X アカウントを
                    tracked account として追加し、その場で初回同期を試みます。利用するには事前に
                    X API credential の登録が必要です。デフォルトはオフです。
                  </div>

                  <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
                    デフォルトは自前描画モードです。画像・動画付きポストを見せたい場合はこちらを推奨します。
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => updateWidgetConfigMutation.mutate(widgetForm)}
                      loading={updateWidgetConfigMutation.isPending}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Widget設定を保存
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-900">埋め込みコード例</p>
                    {(trackedAccounts.length ? trackedAccounts : [{ screen_name: "USERNAME" } as TrackedAccount]).map(
                      (account) => (
                        <div key={account.screen_name} className="rounded-lg bg-gray-50 p-4">
                          <p className="mb-2 text-sm font-medium text-gray-700">
                            @{account.screen_name}
                          </p>
                          <code className="block break-all text-sm text-gray-800">
                            {widgetSnippet(account.screen_name)}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-3"
                            onClick={() => copyText(widgetSnippet(account.screen_name), account.screen_name)}
                          >
                            {copied === account.screen_name ? (
                              <>
                                <Check className="mr-2 h-4 w-4 text-emerald-600" />
                                コピー済み
                              </>
                            ) : (
                              <>
                                <Copy className="mr-2 h-4 w-4" />
                                コードをコピー
                              </>
                            )}
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        ) : null}
      </div>
    </DashboardLayout>
  );
};
